import { useTranslation } from 'react-i18next';

import { HeadingRow } from '@vestigas/ui';

import {
  CostCenterSignatureRatesChart,
  DateRangeSelect,
  DeliveryNoteActivityChart,
  DeliveryNoteKpis,
  SiteSignatureRatesChart,
  UsageDetailsTable,
  UserActivityChart,
  UserKpis,
} from './components';

/**
 * Usage Analytics Page
 *
 * Layout uses CSS Grid with container queries (@container/usage).
 * Breakpoint: @3xl/usage switches from 1-column to 2-column layout.
 *
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │                        SMALL SCREENS (1 column)                         │
 * ├─────────────────────────────────────────────────────────────────────────┤
 * │ [1] General Usage Heading                                               │
 * ├─────────────────────────────────────────────────────────────────────────┤
 * │ [2] KPI: Users Registered    │ KPI: Users Active                        │
 * ├─────────────────────────────────────────────────────────────────────────┤
 * │ [3] User Activity Chart                                                 │
 * ├─────────────────────────────────────────────────────────────────────────┤
 * │ [4] Delivery Notes Heading                                              │
 * ├─────────────────────────────────────────────────────────────────────────┤
 * │ [5] KPI: Received    │ KPI: Assigned    │ KPI: Signed                   │
 * ├─────────────────────────────────────────────────────────────────────────┤
 * │ [6] Delivery Note Activity Chart                                        │
 * ├─────────────────────────────────────────────────────────────────────────┤
 * │ [7] Signature Rates Heading                                             │
 * ├─────────────────────────────────────────────────────────────────────────┤
 * │ [8] Site Signature Rates Chart                                          │
 * ├─────────────────────────────────────────────────────────────────────────┤
 * │ [9] Cost Center Signature Rates Chart                                   │
 * ├─────────────────────────────────────────────────────────────────────────┤
 * │ [10] Usage Details Table                                                │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │                       LARGE SCREENS (2 columns)                         │
 * ├──────────────────────────────────┬──────────────────────────────────────┤
 * │ [1] General Usage Heading        │ [2] Delivery Notes Heading           │
 * ├──────────────────────────────────┼──────────────────────────────────────┤
 * │ [3] KPI: Registered │ KPI: Active│ [4] KPI: Received │ Assigned │ Signed│
 * ├──────────────────────────────────┼──────────────────────────────────────┤
 * │ [5] User Activity Chart          │ [6] Delivery Note Activity Chart     │
 * ├──────────────────────────────────┴──────────────────────────────────────┤
 * │ [7] Signature Rates Heading                                             │
 * ├──────────────────────────────────┬──────────────────────────────────────┤
 * │ [8] Site Signature Rates Chart   │ [9] Cost Center Signature Rates      │
 * ├──────────────────────────────────┴──────────────────────────────────────┤
 * │ [10] Usage Details Table                                                │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * Numbers in brackets [n] indicate the CSS order property value.
 */
export const UsageAnalytics = () => {
  const { t } = useTranslation('analytics');

  return (
    <div className="min-h-screen w-full bg-white">
      <div className="mx-auto max-w-404 px-8">
        <DateRangeSelect className="sticky -top-4 z-30 bg-white/60 pt-8 pb-4 backdrop-blur-sm" />

        <div className="@container/usage flex flex-col pt-2 pb-8">
          <div className="grid auto-rows-[minmax(0,auto)] grid-cols-1 gap-4 @3xl/usage:grid-cols-2">
            <HeadingRow
              heading={t(($) => $.usage.generalUsage.heading)}
              variant="h2"
              className="order-1 -mb-2 @3xl/usage:order-1"
            />
            <UserKpis className="order-2 @3xl/usage:order-3" />
            <UserActivityChart className="order-3 row-span-3 grid-rows-subgrid @3xl/usage:order-5" />

            <HeadingRow
              heading={t(($) => $.usage.deliveryNotes.heading)}
              variant="h2"
              className="order-4 mt-8 -mb-2 @3xl/usage:order-1 @3xl/usage:mt-0"
            />
            <DeliveryNoteKpis className="order-5 @3xl/usage:order-4" />
            <DeliveryNoteActivityChart className="order-6 row-span-3 grid-rows-subgrid @3xl/usage:order-6" />

            <HeadingRow
              heading={t(($) => $.usage.signatureRates.heading)}
              variant="h2"
              className="order-7 col-span-full mt-8 -mb-2 @3xl/usage:order-7"
            />
            <SiteSignatureRatesChart className="order-8 row-span-3 grid-rows-subgrid @3xl/usage:order-8" />
            <CostCenterSignatureRatesChart className="order-9 row-span-3 grid-rows-subgrid @3xl/usage:order-9" />

            <UsageDetailsTable className="order-10 col-span-full mt-12 @3xl/usage:order-10" />
          </div>
        </div>
      </div>
    </div>
  );
};
